import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Layout from "./Layout";
import { apiPost } from "../Utils/apiFetch";
import apiPath from "../Utils/apiPath";

const AffiliateLink = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    shouldFocusError: true,
  });

  const [showPassword, setShowPassword] = useState({
    first: false,
    second: false,
  });
  const handleShow = (id) =>
    setShowPassword({ ...showPassword, [id]: !showPassword[id] });

  // Re-validate confirmPassword when password changes
  useEffect(() => {
    if (!isEmpty(watch("confirmPassword"))) {
      if (watch("password")) {
        trigger("confirmPassword");
      }
    }
  }, [watch("password")]);

  // Pre-fill referral code from URL query params
  useEffect(() => {
    if (!isEmpty(search)) {
      if (search?.split("=")[0] === "?referral_code") {
        setValue("referral_code", search?.split("=")[1]);
      }
    }
  }, [search]);

  const onSubmit = async (body) => {
    let hostname = window.location.hostname;
    hostname = hostname.replace(/^www\./, "");
    hostname = hostname.replace(/^ag\./, "");
    const { status, data } = await apiPost(apiPath.affiliate, {
      whatsappNumber: body?.mobile,
      username: body?.username,
      password: body?.password,
      confirmPassword: body?.confirmPassword,
      referral_code: body?.referral_code || "",
      website: hostname || "bdsports88.com",
    });
    if (status === 200 && data.success) {
      toast.success(data?.message);
      navigate("/");
    } else {
      toast.error(data?.message);
    }
  };

  const renderField = ({
    label,
    required,
    name,
    type = "text",
    placeholder,
    rules,
    toggle,
  }) => (
    <>
      <div className="px-2 change-password-row">
        <div
          className="no-gutters"
          style={{ flex: "0 0 34%", maxWidth: "34%", padding: "0 0 0 12px" }}
        >
          <label className="white__text" style={{ fontSize: 14 }}>
            {label}
            {required && <span style={{ color: "red" }}> *</span>}
          </label>
        </div>
        <div
          className="v-input change-password-input theme--light v-text-field v-text-field--is-booted v-text-field--placeholder"
          style={{ flex: 1, minWidth: 0 }}
        >
          <div className="v-input__control">
            <div className="v-input__slot">
              <div className="v-text-field__slot">
                <input
                  type={toggle ? (showPassword[toggle] ? "text" : "password") : type}
                  autoComplete="new-password"
                  placeholder={placeholder}
                  {...register(name, rules)}
                />
              </div>
              {toggle && (
                <div className="v-input__append-inner">
                  <div className="v-input__icon v-input__icon--append">
                    <button
                      type="button"
                      aria-label={t("AppendIcon")}
                      onClick={() => handleShow(toggle)}
                      className="v-icon notranslate v-icon--link material-icons theme--light"
                    >
                      remove_red_eye
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="v-text-field__details">
              <div className="v-messages theme--light">
                <div className="v-messages__wrapper">
                  {!isEmpty(errors?.[name]?.message) && (
                    <div style={{ marginTop: "3px" }} className="alert">
                      {errors[name].message}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="profile-divider"></div>
    </>
  );

  return (
    <Layout>
      <div className="change-password-container">
        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate="novalidate"
          className="v-form mobile"
        >
          <div className="pa-2 col col-12">
            <div className="change-password-wrapper">
              <h3 className="text-center my-2">{t("AffiliateTitle")}</h3>

              {renderField({
                label: t("Username"),
                required: true,
                name: "username",
                placeholder: t("CharactersPlaceholder"),
                rules: {
                  required: t("Enter_Valid_Username"),
                  validate: {
                    noLeadingSpaces: (value) =>
                      !/^\s/.test(value) || t("Enter_Valid_Username"),
                    noTrailingSpaces: (value) =>
                      !/\s$/.test(value) || t("Enter_Valid_Username"),
                    alphanumericOnly: (value) =>
                      /^[a-zA-Z0-9 ]*$/.test(value) ||
                      t("Only_Alphabets_And_Numbers_Allowed"),
                    noOnlySpaces: (value) =>
                      value.trim().length > 0 || t("Enter_Valid_Username"),
                    mustContainAlphabet: (value) =>
                      /[a-zA-Z]/.test(value) ||
                      t("Username_Must_Contain_At_Least_One_Letter"),
                    minLength: (value) =>
                      value.trim().length >= 3 || t("Min3User"),
                    maxLength: (value) =>
                      value.trim().length <= 30 || t("Max30User"),
                  },
                },
              })}

              {renderField({
                label: t("Password"),
                required: true,
                name: "password",
                toggle: "first",
                placeholder: t("PasswordPlaceholder"),
                rules: {
                  required: t("PasswordRequired"),
                  minLength: { value: 6, message: t("PasswordBetween") },
                  maxLength: { value: 20, message: t("PasswordBetween") },
                  validate: {
                    hasAlphabet: (value) =>
                      /[a-zA-Z]/.test(value) || t("PasswordAtLeastAlphabet"),
                    hasNumber: (value) =>
                      /[0-9]/.test(value) || t("PasswordAtLeastNumber"),
                  },
                },
              })}

              {renderField({
                label: t("Confirm_Password"),
                required: true,
                name: "confirmPassword",
                toggle: "second",
                placeholder: t("Confirm_Password"),
                rules: {
                  required: t("Enter_CPassword"),
                  validate: (value) => {
                    if (!isEmpty(watch("password"))) {
                      return value === watch("password") || t("PasswordMatch");
                    }
                  },
                },
              })}

              {renderField({
                label: t("PhoneNumber"),
                required: true,
                name: "mobile",
                type: "number",
                placeholder: t("FillUpHere"),
                rules: {
                  required: t("Entervalidnumber"),
                  minLength: { value: 10, message: t("MobileMin") },
                  maxLength: { value: 12, message: t("MobileMax") },
                },
              })}

              {renderField({
                label: t("ReferralCode"),
                name: "referral_code",
                placeholder: t("Optional"),
              })}

              <div className="px-3 my-3" style={{ fontSize: 13 }}>
                <input
                  type="checkbox"
                  className="register-checkbox"
                  style={{ marginRight: 6 }}
                />
                <span>{t("Iagreeunderstandthe")} </span>
                <a href="#"> {t("TC")}</a>
              </div>

              <div className="full-width d-flex justify-center">
                <button
                  type="submit"
                  className="change-password-button v-btn v-btn--is-elevated v-btn--has-bg theme--light v-size--default"
                >
                  <span className="v-btn__content">{t("Submit")}</span>
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default AffiliateLink;
